import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { TextField, Checkbox, FormControlLabel, MenuItem, Select, InputLabel, FormControl } from '@material-ui/core';
import api from 'api';
import { Button } from 'components/common/Button/Button';
import { selectSelectedSupplierId } from 'store/suppliersList/Selector';
import { startLoading, finishLoading } from 'hooks/useProgress';
import { SupplierId } from 'store/suppliersList/types';
import { AppState } from 'store/RootReducer';
import { showSnackbar } from 'hooks/useSnackbar';
import cn from './FleetTab.module.scss';

type MapStateToProps = {
  selectedSupplierId: SupplierId;
};

type FleetTabProps = MapStateToProps;

type Fleet = {
  id: number;
  supplierCardId: string;
  euro7Pct: number | null;
  euro6Pct: number | null;
  euro5Pct: number | null;
  euro4OrOlderPct: number | null;
  trailerStandard: boolean;
  trailerMega: boolean;
  trailerRefrigerated: boolean;
  trailerFlatbed: boolean;
  trailerCoilMulden: boolean;
  trailerOther: boolean;
  trailerOtherDescription: string;
  fuelDiesel: boolean;
  fuelHVO: boolean;
  fuelBiomethane: boolean;
  fuelElectric: boolean;
  fuelHydrogen: boolean;
  fuelOther: boolean;
  fuelOtherDescription: string;
  fossilDieselPct: number | null;
  alternativeFuelsPct: number | null;
  reduceFossilPlan: number;
  lastUpdated: string | null;
  lastUpdatedBy: string | null;
};

const initialFleet: Fleet = {
  id: 0,
  supplierCardId: '',
  euro7Pct: null,
  euro6Pct: null,
  euro5Pct: null,
  euro4OrOlderPct: null,
  trailerStandard: false,
  trailerMega: false,
  trailerRefrigerated: false,
  trailerFlatbed: false,
  trailerCoilMulden: false,
  trailerOther: false,
  trailerOtherDescription: '',
  fuelDiesel: false,
  fuelHVO: false,
  fuelBiomethane: false,
  fuelElectric: false,
  fuelHydrogen: false,
  fuelOther: false,
  fuelOtherDescription: '',
  fossilDieselPct: null,
  alternativeFuelsPct: null,
  reduceFossilPlan: 0,
  lastUpdated: null,
  lastUpdatedBy: null,
};

const trailerFields = [
  { key: 'trailerStandard', label: 'Standard trailer' },
  { key: 'trailerMega', label: 'Mega / jumbo trailer' },
  { key: 'trailerRefrigerated', label: 'Refrigerated / temperature-controlled' },
  { key: 'trailerFlatbed', label: 'Flatbed / low loader' },
  { key: 'trailerCoilMulden', label: 'Coil / mulden' },
  { key: 'trailerOther', label: 'Other (please specify below)' },
] as const;

const fuelFields = [
  { key: 'fuelDiesel', label: 'Diesel (fossil)' },
  { key: 'fuelHVO', label: 'HVO (hydrotreated vegetable oil)' },
  { key: 'fuelBiomethane', label: 'Biomethane / CNG / LNG' },
  { key: 'fuelElectric', label: 'Electric' },
  { key: 'fuelHydrogen', label: 'Hydrogen' },
  { key: 'fuelOther', label: 'Other (please specify below)' },
] as const;

const FleetTabComponent = React.memo(({ selectedSupplierId }: FleetTabProps) => {
  const [fleet, setFleet] = useState<Fleet>(initialFleet);

  const requestData = useCallback(async () => {
    startLoading();
    try {
      const data = await api.getFleet(selectedSupplierId);
      setFleet(data || initialFleet);
    } catch (err) {}
    finishLoading();
  }, [selectedSupplierId]);

  useEffect(() => {
    if (selectedSupplierId) {
      requestData();
    }
  }, [selectedSupplierId, requestData]);

  function handlePctChange(field: string, value: string): void {
    const num = value === '' ? null : parseFloat(value);
    setFleet({ ...fleet, [field]: num });
  }

  function handleCheckChange(field: string, checked: boolean): void {
    setFleet({ ...fleet, [field]: checked });
  }

  async function handleSave(): Promise<void> {
    startLoading();
    try {
      const { id, supplierCardId, ...payload } = fleet;
      await api.saveFleet(selectedSupplierId, payload);
      showSnackbar({ message: 'Fleet data saved.', type: 'success' });
    } catch (err) {}
    finishLoading();
  }

  return (
    <div className={cn.fleetContainer}>
      {fleet.lastUpdated && (
        <div className={cn.lastUpdated}>
          Last updated: {new Date(fleet.lastUpdated).toISOString().split('T')[0]}{fleet.lastUpdatedBy && <><br />by {fleet.lastUpdatedBy}</>}
        </div>
      )}
      <div className={cn.section}>
        <div className={cn.sectionTitle}>Engine emission standard (approx. % of fleet)</div>
        <div className={cn.pctRow}>
          {[
            { key: 'euro7Pct', label: 'EURO 7' },
            { key: 'euro6Pct', label: 'EURO 6' },
            { key: 'euro5Pct', label: 'EURO 5' },
            { key: 'euro4OrOlderPct', label: 'EURO 4 or older' },
          ].map(f => (
            <TextField
              key={f.key}
              label={f.label}
              type="number"
              value={(fleet as any)[f.key] ?? ''}
              onChange={(e) => handlePctChange(f.key, e.target.value)}
              variant="outlined"
              size="small"
              className={cn.pctField}
              inputProps={{ min: 0, max: 100 }}
            />
          ))}
        </div>
      </div>

      <div className={cn.section}>
        <div className={cn.sectionTitle}>Trailer types operated (tick all that apply)</div>
        <div className={cn.checkRow}>
          {trailerFields.map(f => (
            <FormControlLabel
              key={f.key}
              control={
                <Checkbox
                  checked={(fleet as any)[f.key] || false}
                  onChange={(e) => handleCheckChange(f.key, e.target.checked)}
                  color="primary"
                />
              }
              label={f.label}
            />
          ))}
        </div>
        {fleet.trailerOther && (
          <TextField
            label="Other vehicle types"
            value={fleet.trailerOtherDescription || ''}
            onChange={(e) => setFleet({ ...fleet, trailerOtherDescription: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
          />
        )}
      </div>

      <div className={cn.section}>
        <div className={cn.sectionTitle}>Primary fuel types used (tick all that apply)</div>
        <div className={cn.checkRow}>
          {fuelFields.map(f => (
            <FormControlLabel
              key={f.key}
              control={
                <Checkbox
                  checked={(fleet as any)[f.key] || false}
                  onChange={(e) => handleCheckChange(f.key, e.target.checked)}
                  color="primary"
                />
              }
              label={f.label}
            />
          ))}
        </div>
        {fleet.fuelOther && (
          <TextField
            label="Other fuel types"
            value={fleet.fuelOtherDescription || ''}
            onChange={(e) => setFleet({ ...fleet, fuelOtherDescription: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
          />
        )}
      </div>

      <div className={cn.section}>
        <div className={cn.inlineField}>
          <span className={cn.inlineLabel}>Approx. % of fleet running on fossil diesel</span>
          <TextField
            type="number"
            value={fleet.fossilDieselPct ?? ''}
            onChange={(e) => handlePctChange('fossilDieselPct', e.target.value)}
            variant="outlined"
            size="small"
            className={cn.inlineInput}
            placeholder="e.g. 90%"
            inputProps={{ min: 0, max: 100 }}
          />
        </div>
      </div>

      <div className={cn.section}>
        <div className={cn.inlineField}>
          <span className={cn.inlineLabel}>Approx. % of fleet running on alternative fuels</span>
          <TextField
            type="number"
            value={fleet.alternativeFuelsPct ?? ''}
            onChange={(e) => handlePctChange('alternativeFuelsPct', e.target.value)}
            variant="outlined"
            size="small"
            className={cn.inlineInput}
            placeholder="e.g. 10%"
            inputProps={{ min: 0, max: 100 }}
          />
        </div>
      </div>

      <div className={cn.section}>
        <div className={cn.inlineField}>
          <span className={cn.inlineLabel}>Do you have a plan to reduce fossil fuel use?</span>
          <FormControl variant="outlined" size="small" className={cn.inlineInput}>
            <Select
              value={fleet.reduceFossilPlan}
              onChange={(e) => setFleet({ ...fleet, reduceFossilPlan: e.target.value as number })}
            >
              <MenuItem value={0}><em>Select...</em></MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
              <MenuItem value={2}>No</MenuItem>
              <MenuItem value={3}>In development</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <div className={cn.buttonRow}>
        <Button onClick={handleSave}>Save</Button>
      </div>
    </div>
  );
});

const mapStateToProps = (state: AppState): MapStateToProps => ({
  selectedSupplierId: selectSelectedSupplierId(state),
});

export const FleetTab = connect(mapStateToProps)(FleetTabComponent);
